/**
 * FormikDatePicker — Drop-in replacement for formik-antd's DatePicker
 *
 * Compatible with Ant Design v6 + dayjs.
 * Binds to Formik via `name` prop using useField().
 * Stores dayjs.Dayjs in the Formik field value.
 */
import React, { useState } from 'react';
import { DatePicker } from 'antd';
import type { DatePickerProps, GetProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useField } from 'formik';

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>;

// ── Helpers ────────────────────────────────────────────────────────────────

function normalizeToDayjs(value: Dayjs | string | null | undefined): Dayjs | null {
  if (value === null || value === undefined) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  if (typeof value === 'string' && value !== '') {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  return null;
}

// ── FormikDatePicker ───────────────────────────────────────────────────────

export type FormikDatePickerProps = Omit<DatePickerProps, 'onChange'> & {
  name: string;
  /** Optional controlled value — overrides Formik field value when provided */
  value?: Dayjs | string | null;
  onChange?: (date: Dayjs | null, dateString: string | string[]) => void;
};

const FormikDatePicker: React.FC<FormikDatePickerProps> & {
  RangePicker: React.FC<FormikRangePickerProps>;
} = ({ name, value: valueProp, onChange, ...rest }) => {
  const [field, , helpers] = useField(name);

  // Use externally-provided value when given, otherwise use Formik field value
  const displayValue = valueProp !== undefined
    ? normalizeToDayjs(valueProp)
    : normalizeToDayjs(field.value);

  const handleChange: DatePickerProps['onChange'] = (date, dateString) => {
    const dayjsDate = date ? normalizeToDayjs(date as any) : null;
    // Only sync to Formik when not fully controlled externally
    if (valueProp === undefined) {
      helpers.setValue(dayjsDate);
    }
    helpers.setTouched(true);
    onChange?.(dayjsDate, dateString ?? '');
  };

  return (
    <DatePicker
      value={displayValue}
      onChange={handleChange}
      onBlur={() => helpers.setTouched(true)}
      {...rest}
    />
  );
};

// ── FormikRangePicker ──────────────────────────────────────────────────────

export type FormikRangePickerProps = Omit<RangePickerProps, 'onChange'> & {
  name: string;
  /** Optional controlled value — overrides Formik field value when provided */
  value?: [Dayjs | null, Dayjs | null] | null;
  onChange?: (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => void;
};

const FormikRangePicker: React.FC<FormikRangePickerProps> = ({ name, value: valueProp, onChange, ...rest }) => {
  const [field, , helpers] = useField(name);
  // Track intermediate state while user is actively picking (only one date selected so far)
  const [pendingValue, setPendingValue] = useState<[Dayjs | null, Dayjs | null] | null | undefined>(undefined);

  const resolvedValue: [Dayjs | null, Dayjs | null] | null = valueProp !== undefined
    ? valueProp
    : Array.isArray(field.value)
      ? [normalizeToDayjs(field.value[0]), normalizeToDayjs(field.value[1])]
      : null;

  // Use pendingValue during active picking so the picker reflects each click immediately
  const displayValue = pendingValue !== undefined ? pendingValue : resolvedValue;

  const handleChange: RangePickerProps['onChange'] = (dates, dateStrings) => {
    const normalized: [Dayjs | null, Dayjs | null] | null = dates
      ? [normalizeToDayjs(dates[0] as any), normalizeToDayjs(dates[1] as any)]
      : null;
    setPendingValue(undefined);
    if (valueProp === undefined) {
      helpers.setValue(normalized);
    }
    helpers.setTouched(true);
    onChange?.(normalized, dateStrings as [string, string]);
  };

  const handleCalendarChange: RangePickerProps['onCalendarChange'] = (dates) => {
    const normalized: [Dayjs | null, Dayjs | null] | null = dates
      ? [normalizeToDayjs(dates[0] as any), normalizeToDayjs(dates[1] as any)]
      : null;
    setPendingValue(normalized);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) setPendingValue(undefined);
  };

  return (
    <DatePicker.RangePicker
      {...rest}
      value={displayValue}
      onChange={handleChange}
      onCalendarChange={handleCalendarChange}
      onOpenChange={handleOpenChange}
      onBlur={() => helpers.setTouched(true)}
    />
  );
};

FormikDatePicker.RangePicker = FormikRangePicker;

export default FormikDatePicker;
